import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaCog,
  FaExclamationTriangle,
  FaFileInvoice,
  FaHandHoldingUsd,
  FaPen,
  FaPlus,
  FaSearch,
  FaWallet,
} from "react-icons/fa";
import { Stat } from "../../pages/dm/components/common";
import { Empty, PageHead } from "../../pages/work/WorkUi";
import PortalSelect from "../PortalSelect";
import billingApi, { billingError } from "./billingApi";
import { InvoiceStatus, fmtDate, money, useBillingMeta } from "./BillingUi";
import { FINANCE_SECTIONS } from "../../utils/financeTerms";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

const FILTERS = [
  { key: "open", label: "Unpaid" },
  { key: "overdue", label: "Overdue" },
  { key: "draft", label: "Drafts" },
  { key: "paid", label: "Paid" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

/** Finance > Invoices: bills this company sends to its own clients. */
function TenantInvoices() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { meta } = useBillingMeta();
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const status = FILTERS.some((f) => f.key === params.get("status")) ? params.get("status") : "open";
  const clientId = params.get("client") || "";
  const canManage = !!meta?.access?.manage;
  const currency = summary?.currency;

  const setQuery = (next) => {
    const merged = { status, ...(clientId ? { client: clientId } : {}), ...next };
    Object.keys(merged).forEach((k) => !merged[k] && delete merged[k]);
    setParams(merged, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        billingApi.summary(),
        billingApi.invoices({ status, client_id: clientId || undefined, search: search || undefined }),
      ]);
      setSummary(s);
      setData(list);
      setError("");
    } catch (err) {
      setError(billingError(err, "Could not load invoices"));
    }
  }, [status, clientId, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="dmp wk bl">
      <PageHead
        title={FINANCE_SECTIONS.invoices.label}
        info={`${FINANCE_SECTIONS.invoices.hint}. Raise an invoice, share it, and record payments on it as they come in.`}
      >
        {canManage ? (
          <>
            <Link className="dmp-btn dmp-btn--ghost" to="/portal/finance/settings">
              <FaCog aria-hidden="true" /> Invoice settings
            </Link>
            <Link className="dmp-btn" to={`/portal/finance/client-invoices/new${clientId ? `?client=${clientId}` : ""}`}>
              <FaPlus aria-hidden="true" /> New invoice
            </Link>
          </>
        ) : null}
      </PageHead>

      <div className="dmp-stats">
        <Link className="wk-tile-btn" to="/portal/finance/receivables">
          <Stat label="Clients owe you" value={summary ? money(summary.outstanding, currency) : "—"} icon={FaWallet} tone="blue" />
        </Link>
        <button type="button" className="wk-tile-btn" onClick={() => setQuery({ status: "overdue" })}>
          <Stat
            label={`Overdue${summary?.overdue_count ? ` (${summary.overdue_count})` : ""}`}
            value={summary ? money(summary.overdue, currency) : "—"}
            icon={FaExclamationTriangle}
            tone="rose"
          />
        </button>
        <Link className="wk-tile-btn" to="/portal/finance/payments-received">
          <Stat
            label="Received this month"
            value={summary ? money(summary.received_this_month, currency) : "—"}
            icon={FaHandHoldingUsd}
            tone="green"
          />
        </Link>
        <button type="button" className="wk-tile-btn" onClick={() => setQuery({ status: "draft" })}>
          <Stat label="Drafts to issue" value={summary ? summary.drafts : "—"} icon={FaPen} tone="amber" />
        </button>
      </div>

      <div className="dmp-card">
        <div className="wk-proj-toolbar">
          <div className="wk-search">
            <FaSearch aria-hidden="true" className="wk-search__icon" />
            <label className="wk-sr-only" htmlFor="bl-search">
              Search invoices
            </label>
            <input
              id="bl-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice number or client"
              autoComplete="off"
            />
          </div>
          <div className="bl-client-filter">
            <PortalSelect value={clientId} onChange={(e) => setQuery({ client: e.target.value })} aria-label="Client" searchable>
              <option value="">All clients</option>
              {(meta?.clients || []).map((c) => (
                <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                  {c.Client_Name}
                </option>
              ))}
            </PortalSelect>
          </div>
        </div>
        <div className="wk-chip-row" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className="wk-chip" aria-pressed={status === f.key} onClick={() => setQuery({ status: f.key })}>
              {f.label}
            </button>
          ))}
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}
        {!data ? (
          <div className="wk-muted">Loading…</div>
        ) : data.invoices.length ? (
          <>
            <table className="wk-table wk-proj-table bl-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Due</th>
                  <th className="bl-num">Total</th>
                  <th className="bl-num">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.Invoice_Id} className="wk-proj-row" onClick={() => navigate(`/portal/finance/client-invoices/${inv.Invoice_Id}`)}>
                    <td data-label="Invoice">
                      <Link
                        className="wk-proj-name"
                        to={`/portal/finance/client-invoices/${inv.Invoice_Id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {inv.Invoice_No || "Draft"}
                      </Link>
                    </td>
                    <td data-label="Client">
                      {inv.Client_Name || "—"}
                      {inv.Project_Name || inv.Service_Name ? (
                        <div className="wk-muted" style={{ fontSize: 12 }}>
                          {inv.Project_Name || inv.Service_Name}
                        </div>
                      ) : null}
                    </td>
                    <td data-label="Date">{fmtDate(inv.Invoice_Date)}</td>
                    <td data-label="Due">
                      {fmtDate(inv.Due_Date)}
                      {inv.Is_Overdue ? <div className="bl-late">{inv.Days_Overdue} days late</div> : null}
                    </td>
                    <td data-label="Total" className="bl-num">
                      {money(inv.Total_Amount, inv.Currency)}
                    </td>
                    <td data-label="Balance" className="bl-num">
                      <strong>{money(inv.Balance_Amount, inv.Currency)}</strong>
                    </td>
                    <td data-label="Status">
                      <InvoiceStatus status={inv.Display_Status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="bl-totals">
              {data.totals.count} invoice(s) · Billed {money(data.totals.billed, currency)} · Still owed{" "}
              <strong>{money(data.totals.balance, currency)}</strong>
            </div>
          </>
        ) : (
          <Empty icon={FaFileInvoice} title={status === "open" ? "Nothing unpaid" : "No invoices here"}>
            {canManage && status !== "cancelled" ? "Raise an invoice to a client with New invoice." : null}
          </Empty>
        )}
      </div>
    </div>
  );
}

export default TenantInvoices;
